import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Hero() {
  const navigate = useNavigate();
  const [activeNode, setActiveNode] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveNode((prev) => (prev + 1) % 5);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const nodes = [
    { label: '平台/收款/报关/发票', x: '10%', y: '50%' },
    { label: '规则校验', x: '30%', y: '50%' },
    { label: '留痕归档', x: '50%', y: '50%' },
    { label: '核销链路', x: '70%', y: '50%' },
    { label: '可追溯', x: '90%', y: '50%' }
  ];

  return (
    <section className="relative min-h-screen flex items-center pt-[72px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/50930b3f72c8f68d1c5d8b329183f45c.jpg"
          alt="background"
          className="w-full h-full object-cover object-top"
        />
        {/* 统一双层遮罩结构 */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/65 via-white/40 to-teal-50/50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/40 to-transparent"></div>
      </div>
      
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* 统一顶部标签徽章样式 */}
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2">
              <i className="ri-global-line text-teal-700"></i>
              <span className="text-sm font-medium text-teal-700">AI驱动 · 全链路合规管理</span>
            </div>

            {/* 统一主标题样式 */}
            <h1 className="text-5xl lg:text-6xl font-extrabold leading-tight">
              <span className="bg-gradient-to-r from-[#0D9488] to-[#14B8A6] bg-clip-text text-transparent drop-shadow-sm">
                AI驱动的
              </span>
              <br />
              <span className="text-gray-800">
                出口合规管理平台
              </span>
            </h1>

            {/* 统一正文样式 */}
            <div className="space-y-3">
              <p className="text-lg text-gray-700 leading-relaxed">
                把出口业务从"能操作"升级为"可解释、可追溯、可复盘"。
              </p>
              <p className="text-lg text-gray-500 leading-relaxed">
                资料归集、规则校验、链路管理与单证归档，一套体系稳定运行。
              </p>
            </div>

            {/* 统一按钮组样式 */}
            <div className="space-y-3 max-w-md pt-4">
              {/* 主按钮：橙色渐变 rounded-full h-14 shadow-lg hover:scale-105 */}
              <button
                onClick={() => navigate('/capabilities')}
                className="w-full h-14 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-medium rounded-full hover:scale-105 transition-transform flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg"
              >
                查看产品及价格
                <i className="ri-arrow-right-line"></i>
              </button>

              {/* 次按钮：bg-white/70 backdrop-blur-sm border-2 border-[#14B8A6] rounded-full h-14 */}
              <button
                onClick={() => navigate('/scenarios')}
                className="w-full h-14 bg-white/70 backdrop-blur-sm border-2 border-[#14B8A6] text-[#0D9488] font-medium rounded-full hover:bg-[#14B8A6] hover:text-white hover:border-[#14B8A6] transition-all whitespace-nowrap cursor-pointer"
              >
                查看场景方案
              </button>

              {/* 文字链接按钮：text-[#0D9488] text-sm h-12 带右上角箭头图标 */}
              <button
                onClick={() => scrollToSection('experience')}
                className="w-full h-12 text-[#0D9488] text-sm font-medium hover:underline transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1"
              >
                先跑通一票（9.9/99）
                <i className="ri-arrow-right-up-line text-base"></i>
              </button>
            </div>

            {/* 统一底部合规边界说明：border-t border-gray-200 pt-6，橙色竖线 + text-xs text-gray-500 */}
            <div className="pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 flex items-start gap-2">
                <span className="w-1 h-4 bg-[#FF6B35] rounded-full flex-shrink-0 mt-0.5"></span>
                <span className="leading-relaxed">
                  <strong className="text-gray-600">合规边界：</strong>提交为工具直连提交+回执回传，不承诺放行/审批结果。
                </span>
              </p>
            </div>
          </div>

          {/* Right Visual - Data Flow Diagram */}
          <div className="relative h-[500px] flex items-center justify-center">
            <div className="relative w-full h-full">
              {/* Connection Lines */}
              <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#0D9488" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#0D9488" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                {nodes.slice(0, -1).map((node, index) => {
                  const nextNode = nodes[index + 1];
                  const isActive = activeNode === index || activeNode === index + 1;
                  return (
                    <line
                      key={index}
                      x1={node.x}
                      y1={node.y}
                      x2={nextNode.x}
                      y2={nextNode.y}
                      stroke={isActive ? '#0D9488' : 'url(#lineGradient)'}
                      strokeWidth={isActive ? '3' : '2'}
                      strokeDasharray={isActive ? '0' : '8,4'}
                      className="transition-all duration-500"
                    />
                  );
                })}
              </svg>

              {/* Nodes */}
              {nodes.map((node, index) => {
                const isActive = activeNode === index;
                return (
                  <div
                    key={index}
                    className="absolute transition-all duration-500"
                    style={{
                      left: node.x,
                      top: node.y,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <div className={`relative flex items-center justify-center transition-all duration-500 ${
                      isActive ? 'scale-110' : 'scale-100'
                    }`}>
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isActive 
                          ? 'bg-gradient-to-br from-[#2DD4BF] to-[#0D9488] shadow-lg shadow-[#14B8A6]/30' 
                          : 'bg-white border-2 border-[#0D9488]/30 shadow-sm'
                      }`}>
                        <i className={`ri-checkbox-circle-line text-2xl transition-colors duration-500 ${
                          isActive ? 'text-white' : 'text-[#0D9488]/60'
                        }`}></i>
                      </div>
                      
                      {isActive && (
                        <div className="absolute inset-0 rounded-full bg-[#2DD4BF] opacity-20 animate-ping"></div>
                      )}
                    </div>

                    <div className={`absolute top-20 left-1/2 -translate-x-1/2 whitespace-nowrap text-center transition-all duration-500 ${
                      isActive ? 'text-[#0D9488] font-semibold' : 'text-gray-500 font-medium'
                    }`}>
                      <div className={`text-xs px-3 py-1.5 rounded-lg border transition-all duration-500 ${
                        isActive 
                          ? 'bg-[#F0FDFA] border-[#0D9488]/40 shadow-sm' 
                          : 'bg-white/80 border-gray-200'
                      }`}>
                        {node.label}
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="absolute inset-0 bg-gradient-to-br from-[#F0FDFA]/40 to-transparent rounded-3xl" style={{ zIndex: 0 }}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}